import { Box, Collapse, FormControlLabel, Stack, Switch, Tooltip } from "@mui/material";
import { MutableRefObject, useCallback, useState } from "react";
import * as TinyMCETypes from "tinymce";
import { QuotaUsageConfirmationTextOrModal } from "../../../App/Components/QuotaMessaging/QuotaMessaging";
import { useMessageDialogState } from "../../../App/State/Hooks/MessageDialog/useMessageDialog";
import { useMessageScheduleHandlers } from "../../../App/State/Hooks/MessageDialog/useMessageSchedule";
import { useAppSelector } from "../../../App/State/Hooks/reduxHooks";
import {
  selectGroupMemberCount,
  selectSelectedCandidateCount,
  selectSelectedCandidatesCountByType,
} from "../../../App/State/Slices/MessageDialogSlice";
import { IMessageContainers } from "../../Enums/MessageEnums";
import { MessageCalendarOptions } from "../../SendGroupMessage/Components/MessageCalendarOptions/MessageCalendarOptions";
import EmailMessageInput from "../../SendGroupMessage/Components/SharedComponents/EmailMessageInput";
import {
  IMessageActionButtonProps,
  MessageActionButton,
} from "../../SendGroupMessage/Components/SharedComponents/MessageActionButton";
import PendingMessageQueue from "../../SendGroupMessage/Components/SharedComponents/PendingMessageQueue";
import { SendMessagesMutationState } from "../State/useMessageMutationState";
import useMessageSendState from "../State/useMessageSendState";
import { useValidateTagsAndRecipients } from "./Hooks/useValidateTagsAndRecipients";
import { Recipients } from "./Recipients";
import { ToggleNotificationAlerts } from "./ToggleNotificationAlerts";

export interface IEmailMessageSendOrScheduleProps {
  editorRef: MutableRefObject<TinyMCETypes.Editor | null>;
  messageMutationState: SendMessagesMutationState;
  isGroup: boolean;
  notifyMe: boolean;
  setNotifyMe: (value: boolean) => void;
  disableScheduling?: boolean;
  disableNotificationToggle?: boolean;
  showCount?: boolean;
}

export default function EmailMessageSendOrSchedule({
  editorRef,
  messageMutationState,
  isGroup,
  notifyMe,
  setNotifyMe,
  disableScheduling = false,
  disableNotificationToggle = false,
  showCount = true,
}: IEmailMessageSendOrScheduleProps) {
  // Hooks - Message state and handlers
  const {
    getters: {
      isScheduled,
      disableSaveMessageAction,
      disableSaveMessageMessage,
      disableScheduleAction,
      disableAddToQueueAction,
      scheduleMessagesTooltipText,
      disabledScheduleMessagesTooltipText,
      addButtonTooltipText,
      postSendMessageIsLoading,
      upsertMessageTemplateLoading,
    },
    handlers: { setIsScheduled, handleSendNow, handleScheduleMessages },
  } = useMessageSendState(messageMutationState);

  const { containerType } = useMessageDialogState();
  const { handleClickScheduleButton } = useMessageScheduleHandlers();

  // Recipient counts
  const selectedCandidateCount = useAppSelector(selectSelectedCandidateCount);
  const groupMemberCount = useAppSelector(selectGroupMemberCount);
  const selectedEmailCandidateCount = useAppSelector((state) =>
    selectSelectedCandidatesCountByType(state, "email"),
  );
  const sendCount = isGroup ? selectedCandidateCount || groupMemberCount : selectedEmailCandidateCount;

  // Validation state
  const validationResults = useValidateTagsAndRecipients(
    disableSaveMessageMessage || "Send Message Now",
    disabledScheduleMessagesTooltipText,
    isGroup,
  );
  const {
    disabledTooltipText,
    disabledScheduleMessagesTooltip,
    hasValidRecipients,
    hasTitleError,
    hasAreaOfInterestError,
  } = validationResults;

  // UI state flags
  const hasValidationErrors = !hasValidRecipients || hasTitleError || hasAreaOfInterestError;
  const hasNoSelectedCandidates = !isGroup && sendCount === 0;
  const disableEmailInput =
    hasNoSelectedCandidates || upsertMessageTemplateLoading || postSendMessageIsLoading;
  const showRecipients = !isGroup && !IMessageContainers[containerType].includes("Candidate");

  // Confirmation modal state
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const openConfirmation = () => setConfirmationModalOpen(true);

  const disableConfirmationAction =
    hasValidationErrors || isScheduled ? disableScheduleAction : disableSaveMessageAction;

  const confirmationAction = isScheduled && !disableScheduling ? handleScheduleMessages : handleSendNow;

  // Handlers
  const handleToggleScheduled = () => {
    setIsScheduled((prev) => !prev);
    handleClickScheduleButton();
  };

  const handleConfirmationActionAndClose = useCallback(async () => {
    await confirmationAction();
    setConfirmationModalOpen(false);
  }, [confirmationAction]);

  // Shared notification alerts component
  const notificationAlerts = (isMultiMessage: boolean) => (
    <ToggleNotificationAlerts
      notifyMe={notifyMe}
      setNotifyMe={setNotifyMe}
      disableNotificationToggle={disableNotificationToggle}
      isMultiMessage={isMultiMessage}
    />
  );

  // Send button props for "Send Now" action
  const sendNowButtonProps = {
    buttonText: "Send Now",
    disableButton: hasValidationErrors || disableSaveMessageAction,
    tooltipText: "Send Message Now",
    disabledTooltipText: disabledTooltipText,
    "data-test": "group-send-now",
    isGroup,
    sendCount,
    showCount,
  } as IMessageActionButtonProps;

  // Render simple send-only mode (scheduling disabled)
  const renderSendOnlyMode = () => (
    <MessageActionButton
      {...sendNowButtonProps}
      onClick={isGroup ? openConfirmation : handleSendNow}
      ToggleNotificationAlerts={notificationAlerts(false)}
    />
  );

  // Render schedule toggle with send/schedule modes
  const renderScheduleMode = () => (
    <Stack spacing={2} direction="row" alignItems="flex-start">
      <Tooltip title="Schedule messages to be sent at a later time" arrow>
        <FormControlLabel
          control={
            <Switch
              data-test="schedule-messages-switch"
              checked={isScheduled}
              onChange={handleToggleScheduled}
              inputProps={{ "aria-label": "controlled" }}
              color="secondary"
            />
          }
          label="Schedule Messages"
          sx={{ flexGrow: { xs: 1, sm: 0 } }}
        />
      </Tooltip>
      <Stack direction="column" sx={{ flexGrow: 1 }}>
        <Collapse in={isScheduled}>
          <MessageCalendarOptions
            addButtonTooltipText={addButtonTooltipText}
            disableAddToQueueAction={disableAddToQueueAction}
          />
        </Collapse>
        <Collapse in={!isScheduled}>
          <MessageActionButton
            {...sendNowButtonProps}
            onClick={openConfirmation}
            ToggleNotificationAlerts={notificationAlerts(false)}
          />
        </Collapse>
      </Stack>
    </Stack>
  );

  return (
    <Box>
      {showRecipients && <Recipients />}
      <EmailMessageInput
        disableInput={disableEmailInput}
        editorRef={editorRef}
        validation={validationResults}
      />

      <Box style={{ marginTop: "20px" }}>
        {disableScheduling ? renderSendOnlyMode() : renderScheduleMode()}
      </Box>

      <Collapse in={isScheduled && !disableScheduling} unmountOnExit>
        <Box>
          <Stack spacing={2} direction="column" alignItems="flex-start" width="100%">
            <PendingMessageQueue />
            <MessageActionButton
              buttonText="Schedule Messages"
              disableButton={hasValidationErrors || disableScheduleAction}
              tooltipText={scheduleMessagesTooltipText}
              disabledTooltipText={disabledScheduleMessagesTooltip}
              data-test="schedule-message-button"
              isGroup={isGroup}
              onClick={openConfirmation}
              sendCount={sendCount}
              showCount={showCount}
              sx={{ width: "100%" }}
              ToggleNotificationAlerts={notificationAlerts(true)}
            />
          </Stack>
        </Box>
      </Collapse>

      <QuotaUsageConfirmationTextOrModal
        channel="email"
        isOpen={confirmationModalOpen}
        setIsOpen={setConfirmationModalOpen}
        isGroup={isGroup}
        selectedCandidatesCount={sendCount}
        isScheduled={isScheduled}
        disableAction={disableConfirmationAction}
        confirmationAction={handleConfirmationActionAndClose}
      />
    </Box>
  );
}
